import { useEffect, useRef, useState } from 'react'

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * Ease a number toward `target` (easeOutCubic, rAF). Unlike useCountUp this RE-RUNS
 * on every target change — animating from the current displayed value to the new one
 * — so chip values re-animate on data refresh as well as on (re)mount. The rAF loop
 * runs only during the transition, then stops (compositor-friendly, no idle work).
 *
 * Under prefers-reduced-motion it initialises to and snaps to the final value with no
 * animation and no re-trigger.
 */
export function useEaseTo(target: number | null, duration = 700): number | null {
  const [value, setValue] = useState<number | null>(() =>
    target == null ? null : prefersReduced() ? target : 0,
  )
  const from = useRef(0)

  useEffect(() => {
    if (target == null) {
      setValue(null)
      return
    }
    if (prefersReduced()) {
      from.current = target
      setValue(target)
      return
    }
    const start = performance.now()
    const begin = from.current
    let raf = 0
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      const v = begin + (target - begin) * eased
      from.current = v
      setValue(v)
      if (p < 1) raf = requestAnimationFrame(tick)
      else from.current = target
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])

  return value
}
